import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { MoveListService } from '@scaffold/services/move-list.service';
import { ItemsService } from '@scaffold/services/items.service';
import { Router, ActivatedRoute } from '@angular/router';
import { ViewModalComponent } from '@scaffold/dialogs/view-modal/view-modal.component';
import { CourseService } from '@scaffold/services/course.service';
import { ConfirmDialog } from '@generic/confirm-dialog/confirm-dialog.component';
import { ViewUpdateService } from '@scaffold/services/view-update.service';
import { VersionControlService } from '@scaffold/services/version-control.service';
import { UserService } from '@services/user.service';
import { take } from 'rxjs/operators';
import { ScaffoldDialogService } from '@app/framework/dialog/scaffold-dialog.service';
import { item } from '@scaffold/editor/items';
import { TranslationService } from '@services/translation.service';

export interface Info {
  type: string;
  id?: string | number;
  [x: string]: any;
}

export interface DataLink {
  new_link: String | Number;
  new_type: String;
  new_mode: String;
  set_position: Number | String;
  link_type?: String;
}

@Injectable({
  providedIn: 'root',
})
export class RenderedService {
  /** Subject to trigger remote updates in the application*/
  private updateTrigger = new Subject<any>();
  /**If the users role disabled editing*/
  private disableEdit: boolean;

  constructor(
    private translationService: TranslationService,
    private moveList: MoveListService,
    private itemsService: ItemsService,
    private router: Router,
    public dialog: ScaffoldDialogService,
    private courseService: CourseService,
    private viewUpdate: ViewUpdateService,
    private versionService: VersionControlService,
    private userService: UserService
  ) {}
  /**
   *Subscribe to update listener
   * */
  listenForUpdates(): Subject<any> {
    return this.updateTrigger;
  }
  /**
   * Trigger an update on listener
   * @param trigger update details
   */
  triggerUpdate(trigger?: string) {
    this.updateTrigger.next(trigger);
  }
  /**
   * Opens a view in a Scaffold dialog
   * @param mode the mode to load
   * @param info the data item info to load
   */
  openView(mode: string, info: Info, autosave?, modalSize?): void {
    console.log('open view', mode, info);
    const settings: any = {
      context: {
        data: {
          size: modalSize,
          mode: mode,
          item: info || info.children,
          parent: { id: this.courseService.id, type: 'course' },
        },
      },
    };
    const dialogRef = this.dialog.open(ViewModalComponent, settings);
    dialogRef.onClose.subscribe(() => {
      if (autosave) {
        this.itemsService.saveData(info)?.pipe(take(1))?.subscribe();
      }
    });
  }

  /**
   * Check if there are any local keys on an object
   * @param obj object to check
   */
  checkKeys(obj: Object): boolean {
    if (obj && Object.keys(obj).length > 0) {
      return true;
    } else {
      return false;
    }
  }
  /**
   * Simple check if a string contains html, if not surround with a P tag
   * @param str String to check
   * @param text Text to return if str not set
   * @param text_margin extra/reduced margin to set around P tag
   */
  hasMarkup(str, text, text_margin): string {
    if (!str) {
      return text;
    }
    str = str.toString();
    if (!str.match(/<[a-z][\s\S]*>/i)) {
      let margin = '';
      if (text_margin) {
        margin = ' style="margin:' + text_margin + 'px 0;"';
      }
      str = `<p${margin}>${str}</p>`;
    }
    return str;
  }
  /**
   * Open the create view of specified data item
   * @param info details about requested item
   */
  openSettings(info: Info) {
    this.openView('create', info);
  }
  /**
   * Open the create view of specified data item
   * @param info details about requested item
   */
  export(info) {
    this.openView('create', info);
  }
  /**
   * Open the read view of specified data item
   * @param info details about requested item
   */
  openRead(info: Info) {
    this.openView('read', info);
  }

  /**
   * Initiate a delete operation on a data item
   * @param info item to be deleted
   */
  deleteData(info: Info) {
    const settings: any = {
      context: {
        data: {
          title:
            this.translationService.getTranslatedContent(
              'delete_item',
              'Delete Item'
            ) + '?',
          message:
            this.translationService.getTranslatedContent(
              'are_you_sure_you_want_to_delete_this_item',
              'Are you sure you want to delete this item'
            ) + '?',
        },
      },
    };
    const dialogRef = this.dialog.open(ConfirmDialog, settings);
    // open confirm dialog
    dialogRef.onClose.subscribe(result => {
      if (result) {
        this.deleteHandler(info);
      }
    });
  }
  /**
   * Send a delete request for a data item to the API
   * @param info data item to delete
   */
  deleteHandler(info: Info) {
    const path = `/${info.type}/${info.id}`;
    if (info.hasOwnProperty('link_id')) {
      if (!info.link_id) {
        info.link_id = info?.links[0]['id'];
      }
      this.unlink(info);
    } else {
      this.itemsService.deleteData(path).subscribe(d => {
        this.viewUpdate.triggerListChange({
          list_type: 'list',
          type: info.type,
          removed: info.id,
        });
      });
    }
  }

  /**
   * Navigate to a route based on an object info
   * @param event click event that triggers the navigation
   * @param item data tiem to navigate to
   * @param activatedRoute current activated route
   * @param target special target for the link
   *                'child' - navigate to referenced_id item
   */
  openLinkHandler(
    event: any,
    info: Info,
    activatedRoute: ActivatedRoute,
    target?: string,
    autosave?,
    modalSize?
  ) {
    event.preventDefault();
    const navConfig = {};
    if (activatedRoute) {
      console.log(event);
      console.log(activatedRoute);
      const relativeTo = activatedRoute?.parent?.component
        ? activatedRoute.parent
        : null;
      if (event.ctrlKey || event.metaKey || event.button === 1) {
        window.open(
          this.router.serializeUrl(
            this.router.createUrlTree([info.type, info.id], { relativeTo })
          ),
          '_blank'
        );
        return false;
      } else {
        navConfig['relativeTo'] = activatedRoute.parent;
      }
    } else {
      if (target === 'item_link') {
        this.openView('read', info, autosave, modalSize);
        return false;
      }
    }
    if (target && target === 'child') {
      this.router.navigate([info.reference_type, info.reference_id], navConfig);
    } else {
      this.router.navigate([info.type, info.id], navConfig);
    }
  }
  /**
   * Check if a data update has errors
   * @param obj object containing data updates
   */
  hasErrors(obj): boolean {
    let errors;
    if (obj?.data_input_error) {
      errors = Object.keys(obj.data_input_error).length;
    }
    return errors > 0 ? true : false;
  }
  /**
   * Create a link between the specified data items
   * @param sourceItem Scaffold component the link originated from
   * @param selectedItem Target data item for the link
   * @param result selected item(s) to link
   * @param position position of the new link
   */
  createLink(
    sourceItem: item,
    selectedItem: Info,
    result: string | Info[],
    position
  ) {
    if (typeof result === 'string') {
      const newLink = {
        new_link: result,
        new_type: sourceItem.toload_type,
        new_mode: sourceItem.toload_mode,
        set_position: position,
      };
      if (sourceItem.type_link) {
        newLink['link_type'] = sourceItem.type_link_value;
      }
      this.save(newLink, selectedItem, res => {
        this.viewUpdate.triggerListChange({
          list_type: 'link',
          parent_id: selectedItem.id,
          type: sourceItem.toload_type,
          added: {
            id: result,
            type: sourceItem.toload_type,
            link_id: res.id,
          },
        });
        const added = {
          id: selectedItem.id,
          type: selectedItem.type,
          link_id: res.link_id,
        };
        this.viewUpdate.triggerListChange({
          list_type: 'link',
          parent_id: result,
          type: selectedItem.type,
          added: added,
        });
      });
    } else {
      result.forEach(info => {
        if (info.linked) {
          if (!info.link_exists) {
            console.log('link does not exist');
            const newLink: DataLink = {
              new_link: info.item.id,
              new_type: sourceItem.toload_type,
              new_mode: sourceItem.toload_mode,
              set_position: position,
            };
            newLink.link_type = sourceItem.type_link_value;
            this.save(newLink, selectedItem, res => {
              this.viewUpdate.triggerListChange({
                list_type: 'link',
                parent_id: selectedItem.id,
                type: sourceItem.toload_type,
                added: {
                  id: info.item.id,
                  type: sourceItem.toload_type,
                  link_id: res.id,
                },
              });
              const added = {
                id: selectedItem.id,
                type: selectedItem.type,
                link_id: res.link_id,
              };
              this.viewUpdate.triggerListChange({
                list_type: 'link',
                parent_id: info.item.id,
                type: selectedItem.type,
                added: added,
              });
            });
          }
        } else {
          if (info.link_exists) {
            this.unlink(info.item);
          }
        }
      });
    }
  }
  /**
   * Save updated data to a data item
   * @param data updates to save
   * @param info item saving to
   * @param complete optional callback function
   */
  save(data, info: Info, complete?) {
    delete data.data_input_error;

    let url = `/${info.type}/${info.id}`;
    if (info.parent) {
      url = `/${info.parent.type}/${info.parent.id}/${info.type}/${info.id}`;
    }
    const tosave: any = {};
    //fix for odd object property issue
    tosave.data = JSON.parse(JSON.stringify(data));
    this.itemsService.saveViewData(url, tosave).subscribe(d => {
      // If this update is adding a new linked data
      if (complete) {
        complete(d);
      }
      for (const prop in data) {
        if (data.hasOwnProperty(prop)) {
          delete data[prop];
        }
      }
      this.triggerUpdate('save');
    });
    for (const prop in data) {
      if (data.hasOwnProperty(prop)) {
        delete data[prop];
      }
    }
  }
  /**
   * Remove a link between data items
   * @param link link to remove
   */
  unlink(link): void {
    if (link.link_id) {
      const path = '/data_link_item/' + link.link_id;
      this.itemsService.deleteData(path).subscribe(d => {
        if (d.deleted) {
          this.viewUpdate.triggerListChange({
            list_type: 'link',
            parent_id: link.link_parent,
            type: link.type,
            removed: link.id,
          });
          this.viewUpdate.triggerListChange({
            list_type: 'link',
            parent_id: link.id,
            type: link.link_parent_type,
            removed: link.link_parent,
          });
        }
      });
    } else {
      console.warn('No link id provided');
    }
    console.log(link);
  }
  /**
   * Get the context of an operation. Provides the parent item to use when searching for data items
   * @param component Configured scaffold component wiht the context setting to look for
   */
  getContext(component: item): Info {
    if (component.parent_context === 'specified') {
      if (component.specified_type && component.value) {
        return {
          type: component.specified_type,
          id: component.value,
        };
      } else {
        return null;
      }
    } else if (component.parent_context === 'all') {
      return null;
    } else {
      return {
        type: 'courses',
        id: this.courseService.id,
      };
    }
  }
  /**
   * Check if editing should be disabled for the current user or if the user is looking at an uneditable version
   * @param type type of disable to check for
   */
  isDisabled(type: string): boolean {
    if (type !== 'users') {
      this.userService
        .getRoleDetails()
        .pipe(take(1))
        .subscribe(role => {
          this.disableEdit = role.prevent_edit || false;
        });
      if (this.disableEdit !== undefined) {
        return this.versionService.currentVersion || this.disableEdit;
      }
    }
    return this.versionService.currentVersion ? true : false;
  }
}
